import React, { useEffect, useState } from 'react';
import { Table, Button, message, Input, Tag, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useProductionCycles } from '../../hooks/useProductionCycles';
import useAuth from '../../hooks/useAuth';

const { Title } = Typography;

const ViewCyclesList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const history = useHistory();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [searchText, setSearchText] = useState('');

  const { data, isLoading, error } = useProductionCycles(tokenData, { company: company.current?.id });

  useEffect(() => {
    if (error) {
      message.error("Error al cargar los ciclos productivos");
      console.log("Error al cargar los ciclos productivos", error);
    }
  }, [error]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'blue';
      case 'PAUSED': return 'orange';
      case 'CLOSED': return 'green';
      case 'CANCELLED': return 'red';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Activo';
      case 'PAUSED': return 'Pausado';
      case 'CLOSED': return 'Cerrado';
      case 'CANCELLED': return 'Cancelado';
      default: return status;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'BATCH': return 'Lote / Batch Industrial';
      case 'HERD': return 'Hato / Lote (Grupal)';
      case 'ANIMAL': return 'Agropecuario (Animales)';
      case 'CROP': return 'Agrícola (Cultivos)';
      case 'EQUIPMENT': return 'Equipo / Línea de Proceso';
      case 'CUSTOM': return 'Personalizado';
      default: return type;
    }
  };

  const columns = [
    {
      title: 'Nombre del Ciclo',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (val: string, record: any) => (
        <div>
          <a 
            style={{ fontWeight: 600, fontSize: 13, color: '#1890ff' }}
            onClick={() => history.push(`/view-cycle/${record.uid || record.id}`)}
          >
            {val}
          </a>
          {record.unit_of_measure && (
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              Capacidad: {record.current_quantity ?? record.initial_quantity ?? 0} {record.unit_of_measure}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Categoría',
      dataIndex: 'cycle_category',
      key: 'cycle_category',
      width: 170,
      render: (val: string) => (
        <Tag color={val === 'FINITE_TRANSFORMATION' ? 'blue' : 'purple'}>
          {val === 'FINITE_TRANSFORMATION' ? 'Transformación Finita' : 'Producción Recurrente'}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'productive_unit_type',
      key: 'productive_unit_type',
      width: 180,
      render: (val: string) => <span style={{ fontSize: 12 }}>{getTypeLabel(val)}</span>
    },
    {
      title: 'Inicio',
      dataIndex: 'start_date',
      key: 'start_date',
      width: 110,
      render: (val: string) => val ? new Date(val).toLocaleDateString() : 'N/A'
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button 
          type="primary" 
          ghost 
          size="small"
          icon={<EditOutlined />} 
          onClick={() => history.push(`/view-cycle/${record.uid || record.id}`)}
        >
          {config.isMobile ? 'Detalle' : 'Ver Detalle'}
        </Button>
      ),
    },
  ];

  const filteredData = data?.cycles?.filter((cycle: any) => 
    cycle.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    cycle.status?.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  return (
    <div style={{ padding: config.isMobile ? '4px 0' : '0' }}>
      <Card bodyStyle={{ padding: config.isMobile ? '14px 10px' : '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: '12px',
          marginBottom: '16px' 
        }}>
          <Title level={config.isMobile ? 4 : 3} style={{ margin: 0 }}>
            Ciclos Productivos
          </Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            style={{ width: config.isMobile ? '100%' : 'auto', height: config.isMobile ? '38px' : 'auto' }}
            onClick={() => history.push('/create-cycle')}
          >
            Nuevo Ciclo
          </Button>
        </div>
        
        <div style={{ marginBottom: '16px', maxWidth: config.isMobile ? '100%' : '380px' }}>
          <Input 
            placeholder="Buscar ciclo (Nombre, Estado)..." 
            prefix={<SearchOutlined />} 
            allowClear
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        <Table 
          columns={columns} 
          dataSource={filteredData} 
          rowKey="id" 
          loading={isLoading}
          size={config.isMobile ? 'small' : 'middle'}
          scroll={{ x: 860 }}
          pagination={{ 
            showSizeChanger: true, 
            defaultPageSize: 10, 
            pageSizeOptions: ['10', '20', '50'],
            size: config.isMobile ? 'small' : 'default'
          }}
        />
      </Card>
    </div>
  );
};

export default ViewCyclesList;
